// Player-id → name resolution for individual-registration tournaments.
//
// Individual formats (americano / mexicano / mixed_americano / king_of_court)
// store player_ids as the match/standings identity. The player NAMES live on the
// roster (team.players[].{id,name}). These helpers build a display-time map and
// resolve ids to names — NO denormalisation into match documents.
//
// All resolution is STRUCTURE-DRIVEN: a side is an array of player_ids, so it
// works identically for 1 id/side (singles → single name) and 2 ids/side
// (doubles → "A & B") with no format check. If a singles format is added later,
// these helpers handle it automatically.

export const INDIVIDUAL_FORMATS = new Set([
  'americano', 'mexicano', 'mixed_americano', 'king_of_court',
]);

// Single source of truth for the individual-registration classification.
// Extend here (one place) if a future individual format like singles is added.
export const isIndividualRegistration = (format) => INDIVIDUAL_FORMATS.has(format);

// Flatten the roster into { player_id: player_name }.
export const buildPlayerById = (teams = []) => {
  const map = {};
  for (const t of teams || []) {
    for (const p of t?.players || []) {
      if (p && p.id) map[p.id] = p.name || p.id;
    }
  }
  return map;
};

const shortLabel = (id) =>
  (typeof id === 'string' && id.startsWith('plr_') ? id.slice(0, 8) : (id || '—'));

// Resolve one identity that may be a player_id (individual standings) or a
// team_id (team formats / single-player teams).
export const resolveEntityName = (id, playerById = {}, teamById = {}, fallback = '—') => {
  if (!id) return fallback;
  return playerById[id] || teamById[id]?.team_name || shortLabel(id);
};

// Resolve a side (array of player_ids) → "Name & Name". Works for 1 or 2 ids.
export const resolveSideNames = (ids, playerById = {}, teamById = {}) => {
  if (Array.isArray(ids) && ids.length) {
    return ids.map((id) => resolveEntityName(id, playerById, teamById)).join(' & ');
  }
  return null;
};

// Resolve a match side preferring player_ids (structure-driven) and falling back
// to the embedded team object / team_id for team formats.
export const resolveMatchSide = ({ playerIds, team, teamId }, playerById = {}, teamById = {}) => {
  const fromPlayers = resolveSideNames(playerIds, playerById, teamById);
  if (fromPlayers) return fromPlayers;
  return team?.team_name || resolveEntityName(teamId, playerById, teamById);
};
